import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, GripVertical } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';

interface Tab {
  id: string;
  index: number;
  title: string;
  weightPct: number;
  active: boolean;
  questions: Question[];
}

interface Question {
  id: string;
  tabId: string;
  text: string;
  order: number;
  points: number;
}

export default function SurveyManager() {
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [editingTab, setEditingTab] = useState<Partial<Tab> | null>(null);
  const [editingQuestion, setEditingQuestion] =
    useState<Partial<Question> | null>(null);
  const [showTabModal, setShowTabModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);

  useEffect(() => {
    loadTabs();
  }, []);

  const loadTabs = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<Tab[]>('/wizard-admin/tabs');
      setTabs(data);
    } catch (error) {
      console.error('Error cargando tabs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTab = () => {
    setEditingTab({
      title: '',
      weightPct: 0,
      index: tabs.length + 1,
      active: true,
    });
    setShowTabModal(true);
  };

  const handleEditTab = (tab: Tab) => {
    setEditingTab(tab);
    setShowTabModal(true);
  };

  const handleSaveTab = async () => {
    if (!editingTab) return;

    try {
      if (editingTab.id) {
        // Actualizar
        await apiClient.put(`/wizard-admin/tabs/${editingTab.id}`, {
          title: editingTab.title,
          weightPct: editingTab.weightPct,
          active: editingTab.active,
        });
      } else {
        // Crear
        await apiClient.post('/wizard-admin/tabs', {
          title: editingTab.title,
          weightPct: editingTab.weightPct,
          index: editingTab.index,
          active: editingTab.active,
        });
      }
      await loadTabs();
      setShowTabModal(false);
      setEditingTab(null);
    } catch (error) {
      console.error('Error guardando tab:', error);
    }
  };

  const handleDeleteTab = async (tabId: string) => {
    if (!confirm('¿Eliminar este tab y todas sus preguntas?')) return;

    try {
      await apiClient.delete(`/wizard-admin/tabs/${tabId}`);
      await loadTabs();
    } catch (error) {
      console.error('Error eliminando tab:', error);
    }
  };

  const handleCreateQuestion = () => {
    if (!selectedTab) return;
    const tab = tabs.find((t) => t.id === selectedTab);
    if (!tab) return;

    setEditingQuestion({
      tabId: selectedTab,
      text: '',
      order: tab.questions.length + 1,
      points: 1,
    });
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async () => {
    if (!editingQuestion || !editingQuestion.tabId) return;

    try {
      if (editingQuestion.id) {
        // Actualizar
        await apiClient.put(`/wizard-admin/questions/${editingQuestion.id}`, {
          text: editingQuestion.text,
          order: editingQuestion.order,
        });
      } else {
        // Crear
        await apiClient.post(
          `/wizard-admin/tabs/${editingQuestion.tabId}/questions`,
          {
            text: editingQuestion.text,
            order: editingQuestion.order,
          }
        );
      }
      await loadTabs();
      setShowQuestionModal(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error('Error guardando pregunta:', error);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('¿Eliminar esta pregunta?')) return;

    try {
      await apiClient.delete(`/wizard-admin/questions/${questionId}`);
      await loadTabs();
    } catch (error) {
      console.error('Error eliminando pregunta:', error);
    }
  };

  const totalWeight = tabs.reduce((sum, tab) => sum + tab.weightPct, 0);
  const weightWarning = Math.abs(totalWeight - 100) > 0.01;

  if (loading) {
    return <div className="p-8 text-center">Cargando...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestión de Surveys
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Administra tabs y preguntas del cuestionario
          </p>
        </div>
        <button
          onClick={handleCreateTab}
          className="flex items-center gap-2 bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nuevo Tab
        </button>
      </div>

      {/* Validación */}
      {weightWarning && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <p className="text-red-700 font-medium">
            ⚠️ Advertencia: El peso total es {totalWeight.toFixed(1)}% (debe ser
            100%)
          </p>
        </div>
      )}

      {/* Lista de Tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar - Tabs */}
        <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-lg font-semibold mb-4">Tabs ({tabs.length})</h2>
          <div className="space-y-2">
            {tabs.map((tab) => (
              <div
                key={tab.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedTab === tab.id
                    ? 'border-primary-dark bg-primary-light/10'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedTab(tab.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-sm">
                        {tab.index}. {tab.title}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 ml-6">
                      Peso: {tab.weightPct}% | {tab.questions.length} preguntas
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTab(tab);
                      }}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <Edit2 className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTab(tab.id);
                      }}
                      className="p-1 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main - Preguntas */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4">
          {selectedTab ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Preguntas - {tabs.find((t) => t.id === selectedTab)?.title}
                </h2>
                <button
                  onClick={handleCreateQuestion}
                  className="flex items-center gap-2 bg-primary-dark text-white px-3 py-2 rounded-lg hover:bg-primary-medium transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Nueva Pregunta
                </button>
              </div>

              <div className="space-y-2">
                {tabs
                  .find((t) => t.id === selectedTab)
                  ?.questions.map((question) => (
                    <div
                      key={question.id}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500">
                              #{question.order}
                            </span>
                            <span className="text-sm text-gray-800">
                              {question.text}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1 ml-8">
                            Puntos: {question.points}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEditQuestion(question)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="p-1 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                {tabs.find((t) => t.id === selectedTab)?.questions.length ===
                  0 && (
                  <div className="text-center py-8 text-gray-500">
                    No hay preguntas. Haz clic en "Nueva Pregunta" para agregar.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Selecciona un tab para ver sus preguntas
            </div>
          )}
        </div>
      </div>

      {/* Modal para Tab */}
      {showTabModal && editingTab && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              {editingTab.id ? 'Editar Tab' : 'Nuevo Tab'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título
                </label>
                <input
                  type="text"
                  value={editingTab.title || ''}
                  onChange={(e) =>
                    setEditingTab({ ...editingTab, title: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peso (%)
                </label>
                <input
                  type="number"
                  value={editingTab.weightPct || 0}
                  onChange={(e) =>
                    setEditingTab({
                      ...editingTab,
                      weightPct: parseFloat(e.target.value),
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingTab.active ?? true}
                  onChange={(e) =>
                    setEditingTab({ ...editingTab, active: e.target.checked })
                  }
                  className="rounded"
                />
                <label className="text-sm text-gray-700">Activo</label>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSaveTab}
                className="flex-1 bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-medium transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                Guardar
              </button>
              <button
                onClick={() => {
                  setShowTabModal(false);
                  setEditingTab(null);
                }}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Pregunta */}
      {showQuestionModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              {editingQuestion.id ? 'Editar Pregunta' : 'Nueva Pregunta'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Texto de la pregunta
                </label>
                <textarea
                  value={editingQuestion.text || ''}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      text: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[100px]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Orden
                </label>
                <input
                  type="number"
                  value={editingQuestion.order || 1}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      order: parseInt(e.target.value),
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSaveQuestion}
                className="flex-1 bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-medium transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                Guardar
              </button>
              <button
                onClick={() => {
                  setShowQuestionModal(false);
                  setEditingQuestion(null);
                }}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
